import { getClassLevel } from './classIdentity.mjs';

// 2024 Druid (SRD 5.2.1). Primal Order is in primalOrder.js, which the spell limits and skill bonuses read synchronously.
const DRUID_FEATURE_LEVELS = Object.freeze({ wildShape: 2, wildResurgence: 5, elementalFury: 7, improvedElementalFury: 15, beastSpells: 18, archdruid: 20 });
export const getDruidLevel = character => getClassLevel(character, 'druid');
export const hasDruidFeature = (character, feature) => getDruidLevel(character) >= (DRUID_FEATURE_LEVELS[feature] ?? Infinity);

export const WILD_SHAPE_FEATURE_ID = 'druid:wild-shape';
export const WILD_COMPANION_FEATURE_ID = 'druid:wild-companion';
export const WILD_RESURGENCE_FEATURE_ID = 'druid:wild-resurgence';
export const NATURE_MAGICIAN_FEATURE_ID = 'druid:nature-magician';
export const PRIMAL_STRIKE_FEATURE_ID = 'druid:primal-strike';
const LONG_RANGE_CANTRIP_BONUS_FEET = 300;

// Druidic (1): Speak with Animals is always prepared, with Druid slots and Wisdom.
export const SPEAK_WITH_ANIMALS_SPELL_ID = 'speak-with-animals';
export const DRUIDIC_GRANT_ID = 'druid-druidic-speak-with-animals';
export const getDruidicSpells = character => (getDruidLevel(character) ? [SPEAK_WITH_ANIMALS_SPELL_ID] : []);

// 2024 Druid Wild Shape (SRD 5.2): two uses from Druid 2, three from 6 and four from 17. Archdruid (20) regains
// a use when you roll Initiative with none left instead of making the uses unlimited.
export const wildShapeUses = (level) => {
  const druidLevel = Math.floor(Number(level) || 0);
  return druidLevel < 2 ? 0 : druidLevel < 6 ? 2 : druidLevel < 17 ? 3 : 4;
};

/**
 * Wild Shape uses, saved at `classState.druid.wildShape` as { spent, revision, slotTaken, converted, form }. `slotTaken`
 * marks Wild Resurgence's use-for-a-slot and `converted` Nature Magician; a Long Rest clears both. `form` is the Beast form
 * held now (beastForm.js).
 */
export const getWildShapeProfile = (character) => {
  const maximum = wildShapeUses(getDruidLevel(character));
  const state = character?.classState?.druid?.wildShape;
  const spent = Math.min(maximum, Math.max(0, Math.floor(Number(state?.spent) || 0)));
  return { maximum, spent, remaining: maximum - spent, revision: Math.floor(Number(state?.revision) || 0), slotTaken: state?.slotTaken === true, converted: state?.converted === true, form: state?.form || null };
};
const nextWildShape = (profile, changes) => ({ spent: profile.spent, slotTaken: profile.slotTaken, converted: profile.converted, ...(profile.form ? { form: profile.form } : {}), ...changes, revision: profile.revision + 1 });
/** The next saved state, with these changes. */
export const wildShapeChanged = (character, changes) => nextWildShape(getWildShapeProfile(character), changes);

/** The next saved state with no Beast form held. */
export const wildShapeWithoutForm = (character) => {
  const { form, ...state } = wildShapeChanged(character, {});
  return state;
};

/** A Short Rest returns one use and a Long Rest all of them; null when nothing changes. */
export const wildShapeRestState = (character, type) => {
  const profile = getWildShapeProfile(character);
  if (!profile.maximum) return null;
  if (type === 'long') return profile.spent || profile.slotTaken || profile.converted ? nextWildShape(profile, { spent: 0, slotTaken: false, converted: false }) : null;
  return profile.spent ? nextWildShape(profile, { spent: profile.spent - 1 }) : null;
};
/** Newer state wins, so a late socket message can't undo a spend. */
export const applyWildShapeUpdate = (character, wildShape) => (!character || !wildShape || Number(wildShape.revision) < getWildShapeProfile(character).revision ? character
  : { ...character, classState: { ...character.classState, druid: { ...character.classState?.druid, wildShape } } });
/** Evergreen Wild Shape (Archdruid): rolling Initiative with no uses left regains one, or null. */
export const evergreenWildShapeState = (character) => {
  const profile = getWildShapeProfile(character);
  return hasDruidFeature(character, 'archdruid') && profile.maximum && !profile.remaining ? nextWildShape(profile, { spent: profile.spent - 1 }) : null;
};
/** Nature Magician (Archdruid): each use converted is worth 2 spell levels. */
export const natureMagicianSlotLevel = uses => uses * 2;

/** Whether a spell record's source is the Druid: the class list, or a feature that counts its spells as Druid spells. */
export const isDruidSpellSource = source => (source?.sourceType === 'class' && source?.sourceId === 'druid') || source?.classId === 'druid';

// Elemental Fury (Druid 7), saved at `classState.druid.elementalFury` as { id }.
export const ELEMENTAL_FURY_OPTIONS = Object.freeze([
  Object.freeze({ id: 'potent-spellcasting', name: 'Potent Spellcasting', summary: 'Add your Wisdom modifier to the damage you deal with any Druid cantrip. At Druid 15, a Druid cantrip with a range of 10 feet or more reaches 300 feet farther.' }),
  Object.freeze({ id: 'primal-strike', name: 'Primal Strike', summary: 'Once on each of your turns when you hit a creature with a weapon or a Beast form’s attack, deal an extra 1d8 Cold, Fire, Lightning or Thunder damage (2d8 at Druid 15).' }),
]);
export const getElementalFury = character => ELEMENTAL_FURY_OPTIONS.find(option => option.id === character?.classState?.druid?.elementalFury?.id) || null;
export const needsElementalFury = character => hasDruidFeature(character, 'elementalFury') && !getElementalFury(character);
export const hasPrimalStrike = character => hasDruidFeature(character, 'elementalFury') && getElementalFury(character)?.id === 'primal-strike';
export const primalStrikeDice = character => (hasDruidFeature(character, 'improvedElementalFury') ? 2 : 1);
export const hasPotentSpellcasting = character => hasDruidFeature(character, 'elementalFury') && getElementalFury(character)?.id === 'potent-spellcasting';
/** Potent Spellcasting's addition to a Druid cantrip's damage. */
export const potentSpellcastingBonus = (character, wisdomModifier) => (hasPotentSpellcasting(character) ? Math.max(0, Math.floor(Number(wisdomModifier) || 0)) : 0);
/** A Druid cantrip's range: Improved Elemental Fury adds 300 feet to one of 10 feet or more. */
export const druidCantripRangeFeet = (character, feet) => (hasPotentSpellcasting(character) && hasDruidFeature(character, 'improvedElementalFury') && Number(feet) >= 10
  ? Number(feet) + LONG_RANGE_CANTRIP_BONUS_FEET : feet);
